var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Search } from '@mui/icons-material';
import { Avatar, Box, Button, Checkbox, InputAdornment, Stack, TextField, Typography, styled } from '@mui/material';
const StyledBorderBox = styled(Box) `
    border: 1px solid ${({ theme }) => theme.palette.other.stroke};
    border-radius: 5px;
`;
const StyledSelectUserListBox = styled(StyledBorderBox) `
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    align-items: flex-start;
    overflow: auto;
    margin-top: 8px;
    padding-bottom: 8px;
`;
const StyledSelectUserListItem = styled(Box) `
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    padding: 0px 6px 0px 0px;

    .MuiAvatar-root {
        width: 24px;
        height: 24px;
        font-size: 0.75rem;
    }
`;
const Root = styled(Stack) `
    height: 100%;
`;
const StyledLabelColumn = styled(Box) `
    display: flex;
    flex-direction: column;
    align-self: center;
    margin-left: 8px;
`;
const LabelTypography = styled(Typography) `
    line-height: 1;
`;
const StyledTextField = styled(TextField) `
    width: 100%;
    height: 36px;
    .MuiInputBase-root {
        height: 36px;
    }
`;
const HeaderRow = styled(Box) `
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;
const ClearAllButton = styled(Button) `
    min-width: max-content;
`;
const SelectAllButton = styled(Button) `
    min-width: max-content;
    margin: 0 8px;
`;
const NoOptionsBox = styled(Box) `
    width: 100%;
    text-align: center;
    margin-top: 8px;
`;
const defaultTexts = {
    searchPlaceholder: 'Search...',
    selectAll: 'Select all',
    clearAll: 'Clear all',
    noOptions: 'No options',
    noSearchResults: 'No search results'
};
export const MultiPicker = (props) => {
    var _a;
    const { list, texts, onChange, searchBy, searchBoxProps, selectAllButtonProps, clearAllButtonProps, userListBoxProps, userListItemProps } = props, other = __rest(props, ["list", "texts", "onChange", "searchBy", "searchBoxProps", "selectAllButtonProps", "clearAllButtonProps", "userListBoxProps", "userListItemProps"]);
    const searchByTitle = searchBy === 'title' || searchBy === 'titleAndSubtitle' || searchBy === undefined;
    const searchBySubtitle = searchBy === 'subtitle' || searchBy === 'titleAndSubtitle' || searchBy === undefined;
    const textsWithDefaults = useMemo(() => (Object.assign(Object.assign({}, defaultTexts), texts)), [texts]);
    const [search, setSearch] = useState('');
    const selectedList = useMemo(() => list.filter((item) => item.selected === true), [list]);
    const handleSelect = (index) => {
        const newList = [...list];
        newList[index].selected = !newList[index].selected;
        onChange(newList);
    };
    const setAll = (selected) => {
        const newList = list.map((item) => {
            item.selected = selected;
            return item;
        });
        onChange(newList);
    };
    const filteredList = useMemo(() => list.filter((item) => {
        var _a, _b;
        return (searchByTitle && ((_a = item.title) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(search.toLowerCase()))) ||
            (searchBySubtitle && ((_b = item.subtitle) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(search.toLowerCase())));
    }), [list, search, searchByTitle, searchBySubtitle]);
    return (_jsxs(Root, Object.assign({ className: 'MultiPicker-root' }, other, { children: [_jsxs(HeaderRow, { className: 'MultiPicker-header', children: [_jsx(StyledTextField, Object.assign({ value: search, onChange: (e) => setSearch(e.target.value), placeholder: (_a = textsWithDefaults.searchPlaceholder) !== null && _a !== void 0 ? _a : 'Search...', InputProps: {
                            startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(Search, {}) }))
                        } }, searchBoxProps)), _jsx(SelectAllButton, Object.assign({ variant: 'outlined', onClick: () => setAll(true) }, selectAllButtonProps, { children: textsWithDefaults.selectAll })), _jsx(ClearAllButton, Object.assign({ variant: 'outlined', onClick: () => setAll(false) }, clearAllButtonProps, { children: textsWithDefaults.clearAll }))] }), _jsxs(StyledSelectUserListBox, Object.assign({ className: 'MultiPicker-users-list' }, userListBoxProps, { children: [filteredList.map((item, index) => {
                        var _a;
                        return (_jsxs(StyledSelectUserListItem, Object.assign({ onClick: () => handleSelect(index) }, userListItemProps, { children: [_jsx(Checkbox, { checked: selectedList.some((selectedId) => selectedId.id === item.id), "data-testid": `checkbox-${item.id}` }), item.avatar && item.avatar, !item.avatar && _jsx(Avatar, { children: ((_a = item.avatarInitials) !== null && _a !== void 0 ? _a : item.title.charAt(0)).toUpperCase() }), _jsxs(StyledLabelColumn, { children: [item.title !== null && item.title !== undefined && item.title.length > 0 && (_jsx(LabelTypography, { children: item.title })), item.subtitle !== null && item.subtitle !== undefined && item.subtitle.length > 0 && (_jsx(LabelTypography, { variant: 'caption', color: 'gray', children: item.subtitle }))] })] }), item.id));
                    }), list.length === 0 && (_jsx(NoOptionsBox, { className: 'MultiPicker-no-options', children: _jsx(Typography, { variant: 'subtitle1', color: 'gray', children: textsWithDefaults.noOptions }) })), list.length > 0 && filteredList.length === 0 && (_jsx(NoOptionsBox, { className: 'MultiPicker-no-search-results', children: _jsx(Typography, { variant: 'subtitle1', color: 'gray', children: textsWithDefaults.noSearchResults }) }))] }))] })));
};
